import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import PageLayout from "../components/PageLayout";
import VehiclePlaquePreview from "../components/plaque/VehiclePlaquePreview";
import PlaqueFieldControls from "../components/plaque/PlaqueFieldControls";
import { PLAQUE_DEFAULTS } from "../utils/vehiclePlaque";
import { resolveVehicleArtwork } from "../utils/vehicleArtwork";
import { loadVehiclePlaque, saveVehiclePlaque } from "../services/vehiclePlaques";
import { artworkObjectUrl, getArtworkVersion, listVehicleArtwork } from "../services/vehicleArtwork";

export default function VehiclePlaque() {
  const { username, carId } = useParams();
  const [loaded, setLoaded] = useState(null);
  const [config, setConfig] = useState(PLAQUE_DEFAULTS);
  const [vin, setVin] = useState("");
  const [artworks, setArtworks] = useState([]);
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [artError, setArtError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const current = ++generation;
      setLoaded(null); setVin(""); setImage(""); setError(""); setLoading(true);
      if (!user) { setError("Sign in as the vehicle owner to open Vehicle Plaque."); setLoading(false); return; }
      try {
        const result = await loadVehiclePlaque(username, carId);
        const library = await listVehicleArtwork();
        if (!active || generation !== current) return;
        const next = { ...PLAQUE_DEFAULTS, ...result.config };
        if (!result.config) {
          const match = resolveVehicleArtwork(result.car, library);
          if (match.status === "matched") {
            next.artworkId = match.candidates[0].id;
            next.artworkVersion = match.candidates[0].currentApprovedVersion;
          }
        }
        setLoaded({ ...result, uid: user.uid }); setConfig(next); setVin(result.vin); setArtworks(library);
      } catch (failure) {
        if (active && generation === current) setError(failure.message || "Unable to load plaque.");
      } finally { if (active && generation === current) setLoading(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, [username, carId]);

  useEffect(() => {
    let active = true;
    let url;
    setImage(""); setArtError("");
    if (loaded && config.artworkId && config.artworkVersion) {
      getArtworkVersion(config.artworkId, config.artworkVersion)
        .then((version) => {
          if (version.status !== "approved") throw new Error("This version is not approved.");
          return artworkObjectUrl(version.assetPath).then((assetUrl) => ({ url: assetUrl, metadata: version }));
        }).then((result) => { url = result.url; if (active) setImage(result); else URL.revokeObjectURL(result.url); })
        .catch(() => { if (active) setArtError("The pinned artwork could not be loaded. Retry or choose approved artwork; your saved selection has not changed."); });
    }
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [loaded, config.artworkId, config.artworkVersion]);

  const changeConfig = (next) => { setConfig(next); setMessage(""); };
  const save = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      const result = await saveVehiclePlaque({ username, carId, config, vin, revision: config.revision });
      setConfig((current) => ({ ...current, revision: result.revision }));
      setMessage("Plaque saved. Your artwork version is pinned.");
    } catch (failure) { setError(failure.message || "Save failed. Your changes remain in this editor."); }
    finally { setSaving(false); }
  };
  const owner = Boolean(loaded && auth.currentUser?.uid === loaded.uid);
  const match = loaded ? resolveVehicleArtwork(loaded.car, artworks) : { candidates: [] };
  const selected = artworks.find((art) => art.id === config.artworkId);
  const newerVersion = selected && selected.currentApprovedVersion !== config.artworkVersion;

  return <PageLayout pageClassName="plaque-page" maxWidth="1280px" showBack eyebrow="Identity Studio" title="Vehicle Plaque"
    subtitle="A lasting identity for your build. Choose the details that tell its story.">
    {loading && <p role="status">Loading your vehicle and private plaque settings…</p>}
    {error && <p className="plaque-error" role="alert">{error}</p>}
    {!loading && !loaded && <Link className="plaque-button" to="/auth">Sign in</Link>}
    {owner && <div className="plaque-editor-layout">
      <section className="plaque-preview-column" aria-label="Preview">
        <div className="plaque-preview-stage"><VehiclePlaquePreview car={loaded.car} config={config} artworkUrl={image?.url || ""} artworkMetadata={image?.metadata} vin={vin} isOwner={owner} /></div>
        <p className="plaque-helper">Live preview · Brushed metal · Export and physical ordering are coming later.</p>
        {artError && <p className="plaque-error" role="alert">{artError}</p>}
        <Link className="plaque-button" to={`/user/${username}/car/${carId}/showcard`}>Back to Identity Studio</Link>
      </section>
      <fieldset className="plaque-controls" disabled={saving} style={{ border: 0, padding: 0, margin: 0 }}>
        <section className="plaque-control-section"><h2>Vehicle silhouette</h2>
          <p className="plaque-helper">Only human-approved masters appear here. Your saved version stays unchanged when new artwork is approved.</p>
          {!match.candidates.length && !config.artworkId && <><p>Vehicle artwork is not available yet.</p>
            <p className="plaque-helper">You can save your details now and return when artwork is approved.</p><Link to="/contact" className="plaque-button">Request vehicle artwork</Link></>}
          {match.status === "selection-required" && !config.artworkId && <p>Artwork selection required. Choose the silhouette that matches your vehicle.</p>}
          <label>Approved artwork<select value={config.artworkId || ""} onChange={(event) => {
            const art = artworks.find((item) => item.id === event.target.value);
            changeConfig({ ...config, artworkId: art?.id || null, artworkVersion: art?.currentApprovedVersion || null });
          }}><option value="">No artwork selected</option>
            {config.artworkId && !match.candidates.some((art) => art.id === config.artworkId) && <option value={config.artworkId}>Saved artwork (pinned)</option>}
            {match.candidates.map((art) => <option key={art.id} value={art.id}>{[art.makeLabel, art.modelLabel, art.generationLabel, art.variantLabel].filter(Boolean).join(" · ")}</option>)}
          </select></label>
          {config.artworkVersion && <p className="plaque-helper">Pinned version: {config.artworkVersion.slice(0, 8)}</p>}
          {newerVersion && <button className="plaque-button" onClick={() => changeConfig({ ...config, artworkVersion: selected.currentApprovedVersion })}>Use newly approved version</button>}
        </section>
        <PlaqueFieldControls car={loaded.car} config={config} onChange={changeConfig} vin={vin} onVinChange={(value) => { setVin(value); setMessage(""); }} />
        <button className="plaque-button primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save plaque"}</button>
        <p role="status" aria-live="polite">{message}</p>
      </fieldset>
    </div>}
  </PageLayout>;
}
